import { useTranslation } from "react-i18next";
import type { PlannedInput } from "../../../../api/content/stepPlans";
import ErrorNotice from "../../../../errors/ErrorNotice";
import { parseCollectionVersionRef } from "../../../../shared/references/contentReferences";
import { useSubmission } from "../../../../shared/state/useSubmission";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import InputCard from "../../../workflows/inputCards/InputCard";
import { plannedCardFacts } from "../../../workflows/inputCards/inputCardFacts";
import { changeThreadSettings } from "../../data/settingsChanges";
import { DocumentSetField, entryReference } from "../../prepare";
import { useThreadContext } from "../threadPageContext";
import { allows } from "../viewerActions";

/**
 * "Which entry?" (F56): the step reads the entry the Thread works on, and the Thread has none,
 * as when it was started from the Workflows page (F28, F31). The host picks a document of the
 * Thread's corpus, and it becomes the Thread's subject, as a start would have set it; module
 * B then plans the step with it.
 */
export default function EntryQuestion({ input }: { input: PlannedInput }) {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  // Module C's decision (W-22 slice 4): the entry is the Run's settings, the host's.
  const choosing = allows(overview.viewer, "change_settings");
  const { state, submit } = useSubmission();
  const corpus = overview.bindings.corpus_version === null ? null : parseCollectionVersionRef(overview.bindings.corpus_version);
  return (
    <InputCard title={t("threads.current.entry.question")} facts={plannedCardFacts(input, null, t)} description={input.description} onEdit={null}>
      {!choosing || corpus === null ? (
        <p className={FORM_HELP_TEXT_CLASS}>{t(choosing ? "threads.current.entry.noCorpus" : "threads.current.entry.hostChooses")}</p>
      ) : (
        <DocumentSetField
          projectId={overview.projectId}
          entries={[]}
          searchIn={corpus}
          disabled={state.status === "submitting"}
          needed={null}
          onChange={([entry]) => {
            if (entry === undefined) return;
            submit(() => changeThreadSettings(threadId, overview.lastEntryNumber, overview.settings, { ...overview.bindings, subject: entryReference(entry) }));
          }}
        />
      )}
      {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
    </InputCard>
  );
}
